import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MarketRequests from './MarketRequests';
import { PsylarisVideoItem } from './PsylarisVideoItem/PsylarisVideoItem';
import usePractitionerControlsContext from '../../../../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import { PsylarisPanelStyle } from './styles';
import ButtonUI from '../../../../UI/Button/Button';
import { useTranslation } from 'react-i18next';
import BackendService from '../../../../../services/BackendService';
import useAppContext from '../../../../../hooks/useAppContext/useAppContext';
import useMediaContext from '../../../../../hooks/useMediaContextHook/useMediaContext';
import PsylarisPannelDetailPopper from './PsylarisPannelDetailPopper/PsylarisPannelDetailPopper';
import MarketSelector from './Markets/Markets';
import { PsylarisListItem } from './PsylarisListItem/PsylarisListItem';
import IconButton from '@mui/material/IconButton';
import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
import Tooltip from '@mui/material/Tooltip';
import { SourceProjection } from '../../../../../commands/practitioner/enums/SourceProjection';

interface PsylarisPanelProps {
  className?: string;
  index: number;
  value: number;
}

interface NestedObject {
  Children?: NestedObject[];
  ThumbnailURL?: string;
  Projection?: string;
  Rotation: number;
  Stereo: string;
}

export function PsylarisPanel(props: PsylarisPanelProps) {
  const { className, value, index, ...other } = props;
  const {
    setOpenMediaModal,
    practitionerMediaURI,
    isMediaLoaded,
    practitionerMediaType,
    projectionType,
    setHasBeenSaved,
    setPractitionerMediaURI,
    setStartResetTimeout,
    setProjectionType,
  } = usePractitionerControlsContext();

  const {
    diveIndex,
    setDiveIndex,
    videos,
    setVideos,
    psylarisDetailModal,
    psylarisDetailInfo,
    detailTitle,
    detailPreview,
    setLastDiveIndex,
    lastDiveIndex,
    selectedItemURL,
    setSelectedItemURL,
    setFirstDiveInd,
    setIsLoading,
    originalMediaURL,
    mediaPopperVideo,
    stereo,
    setStereo,
    rotation,
    setRotation,
  } = useMediaContext();
  const [checkedIndex, setCheckedIndex] = useState<number | null>(null);
  const [lastNestedComponent, setLastNestedComponent] = useState(false);
  const { t, i18n } = useTranslation();
  const { psylarisContainer } = PsylarisPanelStyle;
  const [isLastNestedItem, setIsLastNestedItem] = useState<boolean>(false);
  const isPlayButtonActive = isLastNestedItem && !!selectedItemURL;
  const backCloseButtonFunction = diveIndex.length > 0;
  const backCloseButtonName = backCloseButtonFunction ? 'media-modal-back' : 'media-modal-cancel';
  const { sessionId, treatmentId, hasDevice } = useAppContext();
  const modalAnchorEl = useRef<HTMLDivElement>(null);
  const [localMediaURI, setLocalMediaURI] = useState<string>('');
  const [selectedLanguageOptions, setSelectedLanguageOptions] = useState<string>('');
  const initialRender = useRef(true);
  const currentLanguage = i18n.language;
  const [showList, setShowList] = useState<boolean>(false);
  const listTooltip = showList ? `${t('on-list')}` : `${t('off-list')}`;

  useEffect(() => {
    switch (currentLanguage) {
      case 'nl':
        setSelectedLanguageOptions('nl-NL');
        break;
      case 'de':
        setSelectedLanguageOptions('de-DE');
        break;
      case 'en':
        setSelectedLanguageOptions('en-US');
        break;
      case 'uk':
        setSelectedLanguageOptions('en-US');
        break;
      default:
        setSelectedLanguageOptions('en-US');
    }
  }, [currentLanguage]);

  const gridData = useMemo(() => {
    let tempdata = tempdataIndex();
    return tempdata.map((itemData: any) => {
      let categoryThumbnailURL;
      let videoURL;
      const titleTranslations = itemData?.Title?.Translations ?? {};
      const descriptionTranslations = itemData?.Description?.Translations ?? {};
      const titleKey = currentLanguage === 'en' || currentLanguage === 'uk' ? 'nl-NL' : selectedLanguageOptions;
      const fallbackKey = 'nl-NL';
      if (itemData?.hasOwnProperty('Children')) {
        categoryThumbnailURL = extractURL(itemData);
      } else {
        categoryThumbnailURL = itemData.ThumbnailURL;
        videoURL = itemData.VideoURL;
      }
      return {
        title: titleTranslations[titleKey] ?? titleTranslations[fallbackKey],
        categoryThumbnailURL,
        videoURL,
        detail: descriptionTranslations[selectedLanguageOptions] ?? descriptionTranslations[fallbackKey],
        ThumbnailURL: itemData.ThumbnailURL,
        stereo: itemData.Stereo,
      };
    });
  }, [videos, diveIndex]);

  useEffect(() => {
    videos.forEach(updateStates);
  }, [videos, diveIndex]);

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      let fetchedVideos;
      switch (currentLanguage) {
        case 'nl':
          fetchedVideos = await MarketRequests.getNetherlandsMarket().then((result) => result.Children);
          break;
        case 'de':
          fetchedVideos = await MarketRequests.getGermanMarket().then((result) => result.Children);
          break;
        case 'en':
          fetchedVideos = await MarketRequests.getNetherlandsMarket().then((result) => result.Children);
          break;
        case 'uk':
          fetchedVideos = await MarketRequests.getNetherlandsMarket().then((result) => result.Children);
          break;
        default:
          fetchedVideos = await MarketRequests.getNetherlandsMarket().then((result) => result.Children);
      }
      setVideos(fetchedVideos);
      setIsLoading(false);
    };
    fetchData();
  }, [currentLanguage]);

  useEffect(() => {
    if (practitionerMediaURI === selectedItemURL) {
      setLocalMediaURI(selectedItemURL);
    }
  }, [practitionerMediaURI]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      const onPlayVideo = async () => {
        if (isMediaLoaded) {
          // Send loading to the practitioner to update the state
          await BackendService.sendMediaStatusReport(sessionId, treatmentId, practitionerMediaURI, 0, 0);
          await BackendService.sendLoadMedia(
            sessionId,
            treatmentId,
            originalMediaURL,
            practitionerMediaType,
            SourceProjection.Flat,
            stereo,
            hasDevice ? rotation : 0
          );
          setPractitionerMediaURI(originalMediaURL);
        }

        setHasBeenSaved(true);
        setSelectedItemURL('');
        setOpenMediaModal(false);
        setStartResetTimeout(true);
      };
      onPlayVideo();
    }
  }, [localMediaURI]);

  const PsylarisPannelDetailPopperMemo = useMemo(
    () => (
      <PsylarisPannelDetailPopper
        psylarisDetailModal={psylarisDetailModal}
        modalAnchorEl={modalAnchorEl}
        content={psylarisDetailInfo}
        title={detailTitle}
        thumbnail={detailPreview}
        lastDiveIndex={lastDiveIndex}
        url={selectedItemURL}
        mediaPopperVideo={mediaPopperVideo}
      />
    ),
    [psylarisDetailModal, modalAnchorEl, psylarisDetailInfo, detailTitle, detailPreview, lastDiveIndex, selectedItemURL]
  );

  function isFakeChild(child: any): boolean {
    return !child || Object.keys(child).length === 0 || !child.Title;
  }

  const onCategoryDive = (index: any) => {
    if (!lastNestedComponent) {
      setIsLoading(true);
      setDiveIndex((prevState) => [...prevState, index]);
    } else {
      setCheckedIndex(index);
      return;
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  function extractURL(obj: NestedObject): string | undefined {
    if (obj.Children) {
      const validChildren = obj.Children.filter((child) => !isFakeChild(child));
      if (validChildren.length > 0) {
        return extractURL(validChildren[0]);
      }
    }
    return obj.ThumbnailURL;
  }

  function tempdataIndex(): NestedObject[] {
    let data = videos;

    diveIndex.forEach((item) => {
      if (data?.[item]?.hasOwnProperty('Children')) {
        const childrenArray = data[item].Children as NestedObject[];
        const validChildren = childrenArray.filter((child: NestedObject) => !isFakeChild(child));
        data = validChildren.length > 0 ? validChildren : [data[item] as NestedObject];
      } else {
        data = [data?.[item] as NestedObject];
      }
    });

    return data;
  }

  const updateStates = () => {
    setIsLoading(true);
    let tempdata = tempdataIndex();
    tempdata.map((itemData: NestedObject) => {
      if (itemData?.hasOwnProperty('Children')) {
        const validChildren = (itemData.Children as NestedObject[]).filter(
          (child: NestedObject) => !isFakeChild(child)
        );

        if (validChildren.length > 0) {
          setLastNestedComponent(false);
          setIsLastNestedItem(false);
          setLastDiveIndex(false);
        } else {
          setLastNestedComponent(true);
          setIsLastNestedItem(true);
          setLastDiveIndex(true);
        }
      } else {
        setRotation(itemData.Rotation);
        setStereo(itemData.Stereo);
        setLastNestedComponent(true);
        setIsLastNestedItem(true);
        setLastDiveIndex(true);
      }

      setIsLoading(false);
    });
  };

  const onCategoryUp = () => {
    if (backCloseButtonFunction) {
      const newArray = [...diveIndex];
      newArray.pop();
      setDiveIndex((prevState) => [...newArray]);
      setFirstDiveInd(true);
      setCheckedIndex(null);
      setSelectedItemURL('');
      setIsLoading(true);
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const onCloseModal = useCallback(() => {
    setOpenMediaModal(false);
    setDiveIndex([]);
  }, []);

  const updateMediaURI = () => {
    setPractitionerMediaURI(selectedItemURL);
  };

  const handleSwitchChange = () => {
    setShowList(!showList);
  };

  return (
    <>
      <Box
        sx={psylarisContainer}
        role="psylarispanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        <Box>
          <Box ref={modalAnchorEl} className={'top-container'}>
            <Typography className={'top-container_title'}>{t('tab-library')}</Typography>
            <Box className="apply-list">
              <Tooltip placement={'right'} title={listTooltip}>
                <IconButton
                  className="list-button"
                  aria-label="fingerprint"
                  onClick={handleSwitchChange}
                  data-testid="pwid-psylaris-list-toggle"
                >
                  <ListAltTwoToneIcon className={showList ? 'list-selected' : 'list'} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box className={'apply-markets'}>
            <MarketSelector />
          </Box>
          <Box className={'content-container'}>
            <Box className={showList ? 'content-container_categories-list' : 'content-container_categories'}>
              {!showList ? (
                <>
                  {gridData.map((item: any, idx: any) => (
                    <PsylarisVideoItem
                      key={idx}
                      itemIndex={idx}
                      title={item.title}
                      onClickHandler={() => onCategoryDive(idx)}
                      imgCategoryUrl={item.categoryThumbnailURL}
                      videoURL={item.videoURL}
                      detail={item.detail}
                      ThumbnailURL={item.ThumbnailURL}
                      stereoMode={item.stereo}
                      selected={idx === checkedIndex}
                    />
                  ))}
                </>
              ) : (
                <>
                  {gridData.map((item: any, idx: any) => (
                    <PsylarisListItem
                      key={idx}
                      itemIndex={idx}
                      title={item.title}
                      onClickHandler={() => onCategoryDive(idx)}
                      imgCategoryUrl={item.categoryThumbnailURL}
                      videoURL={item.videoURL}
                      detail={item.detail}
                      ThumbnailURL={item.ThumbnailURL}
                      stereoMode={item.stereo}
                      selected={idx === checkedIndex}
                    />
                  ))}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className={'apply-buttons'}>
        <ButtonUI
          className={'cancel-btn'}
          text={t(backCloseButtonName)}
          onClickHandler={backCloseButtonFunction ? onCategoryUp : onCloseModal}
          variant={'outlined'}
          dataTestId="pwid-media-modal-cancel"
        />
        <ButtonUI
          className={'save-button'}
          text={t('media-modal-save')}
          onClickHandler={updateMediaURI}
          disabled={!isPlayButtonActive}
          dataTestId="pwid-media-modal-save"
        />
        {PsylarisPannelDetailPopperMemo}
      </Box>
    </>
  );
}
